/**
 * „Vorschlag übernehmen“ im Reminder-Editor: setzt den Vorschlag eines KI-Zeitcheck-Befunds an die Stelle
 * des Zitats — NUR, wenn die Stelle eindeutig ist. Gespeichert wird danach von Hand.
 *
 * Die KI bekommt den GERENDERTEN, normalisierten Text (node-app `zeitbezug.ts`: `kiEingabe` →
 * `sichtbarerText`): Platzhalter sind aufgelöst, geschützte und mehrfache Leerzeichen vereinheitlicht.
 * Gesucht wird deshalb normalisiert, ersetzt im Original. Ein Zitat, das über Tags reicht oder einen
 * gerenderten Platzhalterwert enthält, wird nicht geraten — dann bleibt es Handarbeit.
 *
 * Das Ergebnis im Text kommt aus dem DOM (Entities dekodiert, Attribute in doppelten Anführungszeichen).
 * Byte-gleich ist der übrige Text damit nicht; TinyMCE normalisiert beim Laden ohnehin.
 */

export type VorschlagFeld = 'subject' | 'preview_text' | 'html_body';

export type VorschlagErgebnis =
  | { ok: true; feld: VorschlagFeld; wert: string }
  | { ok: false; grund: 'leer' | 'nicht_gefunden' | 'mehrdeutig' | 'ueber_grenzen' };

export interface VorschlagFelder {
  subject?: string | null;
  preview_text?: string | null;
  html_body?: string | null;
}

/** Normalisiert wie `sichtbarerText` und merkt sich je Zeichen die Stelle im Original. */
function normMitKarte(s: string): { text: string; karte: number[] } {
  let text = '';
  const karte: number[] = [];
  let leer = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i] === ' ' ? ' ' : s[i];
    if (/\s/.test(c)) {
      if (leer) continue;
      leer = true;
      text += ' ';
    } else {
      leer = false;
      text += c;
    }
    karte.push(i);
  }
  return { text, karte };
}

const norm = (s: string) => normMitKarte(s).text.trim();

/** Alle Fundstellen als [Start, Ende) im ORIGINAL. */
function fundstellen(original: string, zitat: string): Array<[number, number]> {
  const { text, karte } = normMitKarte(original);
  const raus: Array<[number, number]> = [];
  for (let p = text.indexOf(zitat); p !== -1; p = text.indexOf(zitat, p + 1)) {
    raus.push([karte[p], karte[p + zitat.length - 1] + 1]);
  }
  return raus;
}

type Treffer = { feld: VorschlagFeld; ersetze: (vorschlag: string) => string };

export function vorschlagEinsetzen(
  felder: VorschlagFelder,
  zitatRoh: string,
  vorschlag: string,
): VorschlagErgebnis {
  const zitat = norm(zitatRoh || '');
  if (!zitat || !String(vorschlag || '').trim()) return { ok: false, grund: 'leer' };

  const treffer: Treffer[] = [];
  for (const feld of ['subject', 'preview_text'] as const) {
    const wert = String(felder[feld] ?? '');
    for (const [von, bis] of fundstellen(wert, zitat)) {
      treffer.push({ feld, ersetze: (v) => wert.slice(0, von) + v + wert.slice(bis) });
    }
  }

  const html = String(felder.html_body ?? '');
  let ueberGrenzen = false;
  if (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const knoten: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) knoten.push(n as Text);
    knoten.forEach((k, i) => {
      for (const [von, bis] of fundstellen(k.data, zitat)) {
        treffer.push({
          feld: 'html_body',
          ersetze: (v) => {
            // Frisch parsen: jeder Treffer arbeitet auf seinem eigenen Dokument.
            const d = new DOMParser().parseFromString(html, 'text/html');
            const w = d.createTreeWalker(d.body, NodeFilter.SHOW_TEXT);
            let j = 0;
            for (let n = w.nextNode(); n; n = w.nextNode(), j++) {
              if (j === i) {
                const t = n as Text;
                t.data = t.data.slice(0, von) + v + t.data.slice(bis);
                break;
              }
            }
            return d.body.innerHTML;
          },
        });
      }
    });
    if (!treffer.some((t) => t.feld === 'html_body')) {
      ueberGrenzen = norm(doc.body.textContent || '').includes(zitat);
    }
  }

  if (treffer.length > 1) return { ok: false, grund: 'mehrdeutig' };
  if (treffer.length === 1)
    return { ok: true, feld: treffer[0].feld, wert: treffer[0].ersetze(vorschlag) };
  return { ok: false, grund: ueberGrenzen ? 'ueber_grenzen' : 'nicht_gefunden' };
}

export const VORSCHLAG_GRUND_TEXT: Record<
  'leer' | 'nicht_gefunden' | 'mehrdeutig' | 'ueber_grenzen',
  string
> = {
  leer: 'Kein Vorschlag vorhanden.',
  nicht_gefunden:
    'Die Stelle steht so nicht im Text (z. B. weil sie einen Platzhalter enthält oder schon geändert wurde) — bitte von Hand ändern.',
  mehrdeutig: 'Die Stelle kommt mehrfach vor — bitte von Hand ändern.',
  ueber_grenzen: 'Die Stelle reicht über eine Formatierung (fett, Link …) — bitte von Hand ändern.',
};
